import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "./store";
import { EntitySliceState, Meta } from "../common/types/commonTypes";
import { CityAndCounty } from "../common/types/apiTypes";
import { listCitiesAndCounties, ReplaceWorkAgreementData } from "./appApi";
import { createQueryAsyncThunk } from "./createApiAsyncThunk";
import { RecruitmentArea } from "../features/settings/recruitment/types";
import { recruitmentAreas } from "../features/settings/recruitment/recruitmentsAPI";

const initialMeta: Meta = {
  status: "idle",
  error: null,
  lastFetched: null,
};

// createEntityAdapter<CityAndCountyEntity> if data contains id field
const citiesAdapter = createEntityAdapter<CityAndCounty, string>({
  selectId: (row: CityAndCounty) => `${row.county}_${row.city}`,
  sortComparer: (a, b) => {
    const byCounty = a.county.localeCompare(b.county);
    if (byCounty !== 0) return byCounty;
    return a.city.localeCompare(b.city);
  },
});

const initialCitiesState: EntitySliceState<CityAndCounty> = {
  ...citiesAdapter.getInitialState(),
  ...initialMeta,
};

const initialState = {
  cities: initialCitiesState,
  replacementWorkAgreementData: null as ReplaceWorkAgreementData,
  expertRecruitmentAreas: {
    status: "idle",
    entities: {} as Record<string, string>,
  },
};

export const fetchCitiesAndCounties = createAsyncThunk<
  CityAndCounty[],
  void,
  { state: RootState }
>("app/fetchCitiesAndCounties", async (_, { requestId }) => {
  const response = await listCitiesAndCounties({
    requestId,
  });
  return response.data;
});

export const fetchExpertRecruitmentAreas = createQueryAsyncThunk<
  RecruitmentArea[],
  { expertId: string }
>("app/fetchExpertRecruitmentAreas", recruitmentAreas);

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setReplacementWorkAgreementData: (
      state,
      action: PayloadAction<ReplaceWorkAgreementData>,
    ) => {
      state.replacementWorkAgreementData = action.payload;
    },
    clearReplacementWorkAgreementData: (state) => {
      state.replacementWorkAgreementData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCitiesAndCounties.pending, (state) => {
        state.cities.status = "pending";
      })
      .addCase(fetchCitiesAndCounties.fulfilled, (state, action) => {
        citiesAdapter.setAll(state.cities, action.payload);
        state.cities.status = "succeeded";
        state.cities.lastFetched = Date.now();
      })
      .addCase(fetchCitiesAndCounties.rejected, (state) => {
        state.cities.status = "failed";
      })
      .addCase(fetchExpertRecruitmentAreas.pending, (state) => {
        state.expertRecruitmentAreas.status = "pending";
      })
      .addCase(fetchExpertRecruitmentAreas.fulfilled, (state, action) => {
        state.expertRecruitmentAreas.status = "succeeded";
        state.expertRecruitmentAreas.entities = action.payload.reduce(
          (acc, area) => {
            acc[area.id] = area.name;
            return acc;
          },
          {} as Record<string, string>,
        );
      })
      .addCase(fetchExpertRecruitmentAreas.rejected, (state) => {
        state.expertRecruitmentAreas.status = "failed";
      });
  },
});

const selectCitiesState = (state: RootState) => state.app.cities;

export const selectExpertRecruitmentAreas = (state: RootState) =>
  state.app.expertRecruitmentAreas.entities;

export const selectCitiesStatus = (state: RootState) =>
  selectCitiesState(state).status;

export const { selectAll: selectAllCities } =
  citiesAdapter.getSelectors<RootState>(selectCitiesState);

export const selectCityOptions = createSelector(
  [
    selectAllCities,
    // state / sortByCounty
    (_: any, sortByCounty?: boolean) => sortByCounty ?? true,
  ],
  (rows, sortByCounty): CityAndCounty[] => {
    if (sortByCounty) return rows;
    return [...rows].sort((a, b) => a.city.localeCompare(b.city, "fi"));
  },
);

export const selectReplacementWorkAgreementData = (state: RootState) =>
  state.app.replacementWorkAgreementData;

export const {
  setReplacementWorkAgreementData,
  clearReplacementWorkAgreementData,
} = appSlice.actions;

export const appReducer = appSlice.reducer;
